import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import axios from "axios";
import { Button } from "../components/Modal";
import { IdentityProviderTypes } from "../identityProviders/Create";
import OrganizationRegistrationLinks from "./OrganizationRegistrationLinks";
import * as XLSX from "xlsx";

interface OrganizationRegistrationProps {
    organization: any;
    hasPermission: (permission: string) => boolean;
    onDeleteIdentityProvider: (id: string) => void;
    onUpdateIdentityProvider: (idp: any) => void;
    onToggleIdentityProviderModal: () => void;
}

const OrganizationRegistration: React.FC<OrganizationRegistrationProps> = ({ organization, hasPermission, onDeleteIdentityProvider, onUpdateIdentityProvider, onToggleIdentityProviderModal }) => {
    const [staffCheckResult, setStaffCheckResult] = React.useState<any>(null);
    const [isCheckingStaff, setIsCheckingStaff] = React.useState(false);

    const checkOrganizationStaff = () => {
        setIsCheckingStaff(true);
        setStaffCheckResult(null);

        axios
            .get(`/Azure/CheckOrganizationStaff/${organization.id}`)
            .then((response) => {
                setStaffCheckResult(response.data);
                setIsCheckingStaff(false);
            })
            .catch((error) => {
                setStaffCheckResult({
                    organizationId: organization.id,
                    organizationName: organization.name,
                    errorMessage: error.response?.data?.error || error.message || "Failed to check organization staff",
                    totalUsers: 0,
                    matchedUsers: 0,
                    unmatchedUsers: 0,
                    errorUsers: 0,
                    userResults: [],
                });
                setIsCheckingStaff(false);
            });
    };

    const exportStaffCheckToExcel = () => {
        if (!staffCheckResult) return;

        // Create summary data
        const summaryData = [
            ["Staff Azure Status Report"],
            ["Organization", staffCheckResult.organizationName],
            ["Organization ID", staffCheckResult.organizationId],
            ["Report Generated", new Date().toLocaleString()],
            [],
            ["Summary Statistics"],
            ["Total Users", staffCheckResult.totalUsers],
            ["Matched", staffCheckResult.matchedUsers],
            ["Not Found", staffCheckResult.unmatchedUsers],
            ["Errors", staffCheckResult.errorUsers],
            [],
        ];

        // Create user details data
        const userDetailsHeaders = ["Status", "Email", "Display Name", "User Principal Name", "Account Enabled", "Entra ID User ID", "Details"];

        const userDetailsData =
            staffCheckResult.userResults?.map((user: any) => [
                user.exists ? "Found" : user.errorMessage ? "Error" : "Not Found",
                user.email || "",
                user.displayName || "-",
                user.userPrincipalName || "-",
                user.exists ? (user.accountEnabled ? "Enabled" : "Disabled") : "-",
                user.entraIdUserId || "-",
                user.errorMessage || (user.exists ? "Matched in Entra ID" : "Not found in Entra ID"),
            ]) || [];

        // Combine all data
        const worksheetData = [...summaryData, ["User Details"], userDetailsHeaders, ...userDetailsData];

        // Create worksheet and workbook
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Staff Check Report");

        // Set column widths
        const maxWidths = worksheetData[0].map((_, colIndex) => {
            return Math.max(
                ...worksheetData.map((row) => {
                    const cell = row[colIndex];
                    return cell ? cell.toString().length : 0;
                }),
            );
        });
        worksheet["!cols"] = maxWidths.map((width) => ({ wch: Math.min(width + 2, 50) }));

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
        const filename = `Staff_Check_${staffCheckResult.organizationName.replace(/[^a-z0-9]/gi, "_")}_${timestamp}.xlsx`;

        // Download file
        XLSX.writeFile(workbook, filename);
    };

    if (!hasPermission("manageOrganizationRegistration")) {
        return null;
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                <FontAwesomeIcon icon={["fad", "user-plus"]} className="text-3xl text-purple-500" />
                <div>
                    <h2 className="text-2xl font-semibold text-gray-800">Registration</h2>
                    <p className="text-sm text-gray-600">Registration links and third-party providers</p>
                </div>
            </div>
            {hasPermission("manageIdentityProviders") && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Identity Providers</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {organization.identityProviders &&
                                    organization.identityProviders.map((identityProvider: any, i: number) => (
                                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {IdentityProviderTypes[identityProvider.type as keyof typeof IdentityProviderTypes]}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{identityProvider.sourceID}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{identityProvider.priority}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end items-center space-x-2">
                                                    {identityProvider.type == 0 && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center px-2.5 py-1.5 border border-blue-300 shadow-sm text-xs font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                onClick={checkOrganizationStaff}
                                                                disabled={isCheckingStaff}
                                                            >
                                                                {isCheckingStaff ? (
                                                                    <>
                                                                        <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-1" />
                                                                        Checking...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FontAwesomeIcon icon={["fad", "users-medical"]} className="mr-1" />
                                                                        Check Staff
                                                                    </>
                                                                )}
                                                            </button>
                                                        </>
                                                    )}
                                                    <Button
                                                        variant={identityProvider.automaticRegistration ? "primary" : "outline-secondary"}
                                                        size="sm"
                                                        onClick={() => onUpdateIdentityProvider({ ...identityProvider, automaticRegistration: !identityProvider.automaticRegistration })}
                                                    >
                                                        Auto Registration {identityProvider.automaticRegistration ? "Enabled" : "Disabled"}
                                                    </Button>
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                        onClick={() => onDeleteIdentityProvider(identityProvider.id)}
                                                    >
                                                        <FontAwesomeIcon icon={["fad", "trash"]} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                <tr>
                                    <td colSpan={4} className="px-6 py-4">
                                        <button onClick={onToggleIdentityProviderModal} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500 font-medium">
                                            <FontAwesomeIcon icon={["fad", "plus-circle"]} className="mr-2" />
                                            Add new Identity Provider
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <OrganizationRegistrationLinks organization={organization.id}></OrganizationRegistrationLinks>

            {/* Staff Check Results */}
            {staffCheckResult && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <FontAwesomeIcon icon={["fad", "users-medical"]} className="text-2xl text-blue-600" />
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Staff Azure Status Report</h3>
                                    <p className="text-sm text-gray-600">{staffCheckResult.organizationName}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={exportStaffCheckToExcel}
                                    className="inline-flex items-center px-3 py-2 border border-green-300 shadow-sm text-sm font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                    title="Export to Excel"
                                >
                                    <FontAwesomeIcon icon={["fad", "file-excel"]} className="mr-2" />
                                    Export to Excel
                                </button>
                                <button onClick={() => setStaffCheckResult(null)} className="text-gray-400 hover:text-gray-600 transition-colors" title="Close report">
                                    <FontAwesomeIcon icon={["fad", "times"]} className="text-xl" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {staffCheckResult.errorMessage ? (
                        <div className="p-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-center text-red-800">
                                    <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="w-5 h-5 mr-2" />
                                    <span className="font-medium">Error checking staff</span>
                                </div>
                                <div className="ml-7 mt-2 text-sm text-red-700">{staffCheckResult.errorMessage}</div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Summary Statistics */}
                            <div className="grid grid-cols-4 gap-4 p-6 border-b border-gray-200">
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-gray-900">{staffCheckResult.totalUsers}</div>
                                    <div className="text-sm text-gray-600 mt-1">Total Users</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-green-600">{staffCheckResult.matchedUsers}</div>
                                    <div className="text-sm text-gray-600 mt-1">Matched</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-yellow-600">{staffCheckResult.unmatchedUsers}</div>
                                    <div className="text-sm text-gray-600 mt-1">Not Found</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-red-600">{staffCheckResult.errorUsers}</div>
                                    <div className="text-sm text-gray-600 mt-1">Errors</div>
                                </div>
                            </div>

                            {/* User Details Table */}
                            <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Display Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UPN</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {staffCheckResult.userResults &&
                                            staffCheckResult.userResults.map((user: any, i: number) => (
                                                <tr key={i} className={`hover:bg-gray-50 transition-colors ${user.exists ? "bg-green-50" : user.errorMessage ? "bg-red-50" : "bg-yellow-50"}`}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {user.exists ? (
                                                            <div className="flex items-center text-green-700">
                                                                <FontAwesomeIcon icon={["fad", "check-circle"]} className="w-5 h-5" />
                                                            </div>
                                                        ) : user.errorMessage ? (
                                                            <div className="flex items-center text-red-700">
                                                                <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="w-5 h-5" />
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center text-yellow-700">
                                                                <FontAwesomeIcon icon={["fad", "question-circle"]} className="w-5 h-5" />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.displayName || "-"}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.userPrincipalName || "-"}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                        {user.exists ? (
                                                            <span
                                                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                    user.accountEnabled ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                                                }`}
                                                            >
                                                                {user.accountEnabled ? "Enabled" : "Disabled"}
                                                            </span>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-xs text-gray-500">
                                                        {user.errorMessage ? (
                                                            <span className="text-red-600">{user.errorMessage}</span>
                                                        ) : user.exists ? (
                                                            <span className="text-gray-400 font-mono">{user.entraIdUserId?.substring(0, 8)}...</span>
                                                        ) : (
                                                            <span className="text-yellow-600">Not found in Entra ID</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default OrganizationRegistration;
